import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ShieldCheck, Database, Calendar, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { OPS } from "@/constants/testIds";

function fmt(v, lang) {
    if (v === null || v === undefined) return "\u2014";
    const loc = lang === "ar" ? "ar-SA" : "en-US";
    return new Intl.NumberFormat(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

export default function FinancialsReconciliationPanel({ signal }) {
    const { t, lang } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        api
            .get("/financials/reconcile")
            .then((r) => active && setData(r.data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [signal]);

    const partials = data?.meta?.partial_months || [];

    return (
        <Card
            data-testid={OPS.financials.reconPanel}
            className="surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)]"
        >
            <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div>
                    <CardTitle className="flex items-center gap-2 text-base font-bold text-[color:var(--ink)]">
                        <ShieldCheck className="h-[18px] w-[18px] text-[color:var(--brand-brown)]" />
                        {t("financials_recon")}
                    </CardTitle>
                    <p className="text-xs text-[color:var(--ink-muted)] mt-1">{t("financials_recon_desc")}</p>
                </div>
                {data?.has_data ? (
                    data.all_ok ? (
                        <Badge className="bg-[color:rgba(31,122,77,0.15)] text-[color:var(--success)] border-0 gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" /> {t("all_matched")}
                        </Badge>
                    ) : (
                        <Badge className="bg-[color:rgba(180,35,24,0.12)] text-[color:var(--danger)] border-0 gap-1">
                            <XCircle className="h-3.5 w-3.5" /> {t("mismatch_found")}
                        </Badge>
                    )
                ) : null}
            </CardHeader>
            <CardContent>
                {loading ? (
                    <Skeleton className="h-[220px] rounded-xl" />
                ) : !data?.has_data ? (
                    <EmptyState icon={Database} title={t("financials_recon_no_data")} hint={t("financials_recon_no_data_hint")} dataset="financials_pl_monthly" />
                ) : (
                    <div className="space-y-3">
                        {/* meta strip */}
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[color:var(--ink-muted)]">
                            {data.ingested_at ? (
                                <span className="inline-flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {t("last_import")}: <bdi dir="ltr">{new Date(data.ingested_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}</bdi>
                                </span>
                            ) : null}
                            {data.meta?.coverage_from ? (
                                <span>
                                    {t("coverage")}: <bdi dir="ltr">{data.meta.coverage_from} → {data.meta.coverage_to}</bdi>
                                </span>
                            ) : null}
                            {data.meta?.source ? (
                                <span>{t("source")}: <bdi dir="ltr">{data.meta.source}</bdi></span>
                            ) : null}
                        </div>

                        {partials.length ? (
                            <div
                                data-testid={OPS.financials.partialBadge}
                                className="inline-flex flex-wrap items-center gap-2 rounded-md bg-[color:rgba(209,128,47,0.12)] px-3 py-1.5 text-xs text-[color:var(--brand-brown)]"
                            >
                                <AlertTriangle className="h-3.5 w-3.5" />
                                <span className="font-semibold">{t("partial_months_label")}:</span>
                                <bdi dir="ltr">{partials.join("، ")}</bdi>
                            </div>
                        ) : null}

                        <div className="overflow-x-auto rounded-lg border border-[color:var(--border-soft)]">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[color:rgba(224,222,217,0.55)] hover:bg-[color:rgba(224,222,217,0.55)]">
                                        <TableHead className="text-start">{t("recon_metric")}</TableHead>
                                        <TableHead className="text-end">{t("recon_stored")}</TableHead>
                                        <TableHead className="text-end">{t("recon_target")}</TableHead>
                                        <TableHead className="text-end">{t("recon_diff")}</TableHead>
                                        <TableHead className="text-center">{t("recon_status")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.lines.map((l) => (
                                        <TableRow
                                            key={l.key}
                                            data-testid={OPS.financials.reconLine(l.key)}
                                            className="hover:bg-[color:rgba(186,217,235,0.18)]"
                                        >
                                            <TableCell className="font-medium text-[color:var(--ink)]">
                                                {lang === "ar" ? l.label_ar : l.label_en}
                                            </TableCell>
                                            <TableCell className="text-end tnum text-[color:var(--ink)]">{fmt(l.stored, lang)}</TableCell>
                                            <TableCell className="text-end tnum text-[color:var(--ink-muted)]">{fmt(l.target, lang)}</TableCell>
                                            <TableCell className={`text-end tnum ${l.diff ? "text-[color:var(--danger)]" : "text-[color:var(--ink-muted)]"}`}>
                                                {fmt(l.diff, lang)}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {l.ok ? (
                                                    <CheckCircle2 className="h-5 w-5 text-[color:var(--success)] inline" />
                                                ) : (
                                                    <XCircle className="h-5 w-5 text-[color:var(--danger)] inline" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
